// ─── La cassa: la giornata, quello che deve esserci, e le righe ──────────────
//
// 🎯 Questa schermata risponde a UNA domanda, ed è la domanda del criterio della
// Fase 6: «quanto deve esserci nel cassetto?». Tutto il resto — l'elenco, il
// filtro, l'export — è il modo di spiegare quel numero quando non torna.
//
// 💶 **E DAL 24/09/2026 È ANCHE LA CASA DEL CONTO COI MAESTRI** (voce 23).
// 🗣️ *«possono stare tutte e due le voci dentro la stessa voce che faremo
// chiamare cassa»*. ⇒ Una voce sola nel menu, con due linguette: **Incassi**
// (la giornata) e **Maestri** (il mese, ⇒ `routes/conto/ContoMaestri.tsx`).
// ⚖️ Linguette e non un numero solo: gli incassi sono soldi che ENTRANO, il
//    conto coi maestri soldi che ESCONO. I soldi delle lezioni sono già nel
//    cassetto, e sommarci il conto li conterebbe due volte.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ETICHETTE_METODO,
  etichettaMetodo,
  METODI_INCASSO,
  formatCents,
  type Incasso,
  type MetodoIncasso,
  type Provenienza,
} from '@pmo/shared';
import { CampoData } from '../../components/CampoData.js';
import { Attesa, Guasto, Vuoto } from '../../components/pezzi.js';
import { GiornoScelto } from '../../components/GiornoScelto.js';
import { useSessione } from '../../features/auth/sessione.js';
import { giornoBreve, giornoPerEsteso, oggi, piuGiorni } from '../../lib/time.js';
import { esportaCassa, usaIncassi, usaRiepilogo, usaStornoIncasso } from '../../features/cassa/dati.js';
import { ContoMaestri } from '../conto/ContoMaestri.js';
import { CampiDaPagare, usaCampiDaPagare } from '../../features/cassa/CampiDaPagare.js';

/**
 * Che periodo si porta via un export, detto per esteso — sta nel `title` dei due
 * bottoni, che è dove lo cerca chi si è già insospettito.
 */
function periodoPerEsteso(da: string, a: string): string {
  return da === a
    ? `Scarica la cassa di ${giornoPerEsteso(da)}`
    : `Scarica la cassa da ${giornoPerEsteso(da)} a ${giornoPerEsteso(a)}`;
}

/**
 * 🗂️ Le due linguette sono due INDIRIZZI (`/cassa` e `/cassa/maestri`), non
 * uno stato della pagina: così il vecchio `/conto-maestri` porta dritto alla
 * sua, e un «indietro» del browser torna dov'era.
 * ⚠️ Cambiando linguetta la giornata scelta negli incassi torna a oggi: ogni
 *    linguetta è una pagina sua, come prima erano due voci del menu.
 */
export function Cassa({ linguetta }: { linguetta: 'incassi' | 'maestri' | 'campi' }): React.JSX.Element {
  const campi = usaCampiDaPagare();
  const quanti = campi.data?.items.length ?? 0;
  return (
    <section className="cassa-unica">
      <header className="testata">
        <h2>Cassa</h2>
      </header>
      <nav className="linguette-cassa" aria-label="Cosa guardi">
        <Link to="/cassa" aria-current={linguetta === 'incassi' ? 'page' : undefined}>
          Incassi <span className="quando">la giornata</span>
        </Link>
        <Link to="/cassa/maestri" aria-current={linguetta === 'maestri' ? 'page' : undefined}>
          Maestri <span className="quando">il mese</span>
        </Link>
        {/* 🔄 30/09/2026: via la linguetta «🌙 Acconti» (i resoconti dell'acconto, andato via). */}
        {/* 🧾 19/10/2026 (mockup «Campi da pagare», approvato). */}
        <Link to="/cassa/campi" aria-current={linguetta === 'campi' ? 'page' : undefined}>
          Campi da pagare{quanti > 0 ? ` (${quanti})` : ''} <span className="quando">da incassare</span>
        </Link>
      </nav>
      {linguetta === 'incassi' ? <Incassi /> : linguetta === 'maestri' ? <ContoMaestri /> : <CampiDaPagare />}
    </section>
  );
}

function Incassi(): React.JSX.Element {
  const { profilo } = useSessione();
  const puòStornare =
    (profilo?.permissions.includes('manage_payments') ?? false) && profilo?.role !== 'readonly';

  const [giorno, setGiorno] = useState(() => oggi());
  const [fino, setFino] = useState<string | null>(null);
  const [metodo, setMetodo] = useState<MetodoIncasso | ''>('');
  const [includiStornati, setIncludiStornati] = useState(true);

  const filtro = {
    from: giorno,
    to: fino ?? giorno,
    ...(metodo === '' ? {} : { method: metodo }),
    includiStornati,
  };
  const riepilogo = usaRiepilogo(filtro.from, filtro.to);
  const righe = usaIncassi(filtro);

  /**
   * ⭐ SFOGLIARE LA GIORNATA, come nel calendario — e non è una somiglianza di
   * gusto: è la stessa domanda («com'è andata ieri?») fatta a due schermate.
   * Prima l'unico modo era **scrivere una data**: per guardare il giorno prima
   * si apriva il calendarietto e si cercava il numero.
   *
   * ⚠️ Spostando il giorno, la fine dell'intervallo si porta dietro sé stessa
   * solo se ha ancora senso: un «fino a» rimasto indietro descriverebbe un
   * intervallo alla rovescia, che il server rifiuta e che nessuno ha chiesto.
   */
  function vaiAlGiorno(g: string): void {
    setGiorno(g);
    if (fino !== null && fino < g) setFino(null);
  }

  return (
    <section className="cassa">
      <div className="barra-cassa">
        {/* 📌 Il giorno sta in testa agli incassi, come nel calendario: quale
            giornata si sta guardando non lo dice nessun altro. Dal 24/09 sopra
            c'è anche il titolo «Cassa», che serve alle due linguette. */}
        <GiornoScelto data={giorno} vaiA={vaiAlGiorno} />

        <div className="navigazione">
          <button
            type="button"
            className="piatto freccia"
            title={`Vai a ${giornoBreve(piuGiorni(giorno, -1))}`}
            aria-label={`Vai a ${giornoBreve(piuGiorni(giorno, -1))}`}
            onClick={() => vaiAlGiorno(piuGiorni(giorno, -1))}
          >
            ←
          </button>
          <button
            type="button"
            className="piatto"
            onClick={() => vaiAlGiorno(oggi())}
            disabled={giorno === oggi()}
          >
            Oggi
          </button>
          <button
            type="button"
            className="piatto freccia"
            title={`Vai a ${giornoBreve(piuGiorni(giorno, 1))}`}
            aria-label={`Vai a ${giornoBreve(piuGiorni(giorno, 1))}`}
            onClick={() => vaiAlGiorno(piuGiorni(giorno, 1))}
          >
            →
          </button>
        </div>

        {/* I filtri stanno in un gruppo loro: quando la barra non ci sta su una
            riga sola deve andare a capo QUI, fra la giornata e come la si
            guarda, non in mezzo ai filtri. */}
        <div className="filtri-cassa">
          {/*
            🗣️🚨⭐⭐ **IL PERIODO SI SCRIVE «DAL … AL …», e fino al 18/09/2026
            c'era un «fino a» solo.** Sue parole, dette due volte: *«devo potergli
            dare un periodo da me stabilito»* e poi, più secca, *«mi serve
            l'export con il periodo dal al»*.
            ⚖️ **Il periodo funzionava già** — provato, `cassa-2026-09-16_2026-09-18.csv`
              con sei righe su tre giornate. Ma il «dal» non era un campo: era
              **la giornata che si stava sfogliando**, dall'altra parte della
              barra, con le frecce. Chi cerca un periodo cerca due date vicine,
              e trovandone una sola conclude che l'altra non c'è.
            ⇒ Adesso le due date stanno **una accanto all'altra**, e sono la
              stessa cosa di prima: «dal» è la giornata (le frecce e «Oggi» la
              muovono ancora, e i due campi restano d'accordo perché il valore è
              **uno solo**), «al» è la fine — vuoto vuol dire una giornata sola.
            📌 *Due comandi che governano una cosa sola vanno messi vicini, o
               diventano due cose.*
          */}
          <div className="campo orizzontale periodo-cassa">
            <span className="tenue piccolo">dal</span>
            <CampoData
              aria-label="dal (primo giorno del periodo)"
              value={giorno}
              onChange={(d) => {
                if (d !== '') vaiAlGiorno(d);
              }}
            />
            <span className="tenue piccolo">al</span>
            <CampoData
              aria-label="al (ultimo giorno del periodo; vuoto = una giornata sola)"
              title="Lascialo vuoto per una giornata sola"
              value={fino ?? ''}
              min={giorno}
              onChange={(d) => setFino(d === '' ? null : d)}
            />
            {fino !== null && (
              /* ⭐ Tornare a una giornata sola dev'essere un gesto, non lo
                 svuotamento a mano di una casella che il browser difende. */
              <button
                type="button"
                className="piatto"
                title="Torna a una giornata sola"
                aria-label="Torna a una giornata sola"
                onClick={() => setFino(null)}
              >
                ✕
              </button>
            )}
          </div>
          <label className="campo orizzontale">
            <span className="tenue piccolo">mezzo</span>
            <select value={metodo} onChange={(e) => setMetodo(e.target.value as MetodoIncasso | '')}>
              <option value="">tutti</option>
              {METODI_INCASSO.map((m) => (
                <option key={m} value={m}>
                  {ETICHETTE_METODO[m]}
                </option>
              ))}
            </select>
          </label>
          {/* 🩹 Qui c'era `className="campo in-linea"`, e quella classe nel foglio
              di stile NON ESISTE: la spunta finiva sopra la sua scritta, in
              mezzo, mentre tutti gli altri campi della barra sono in riga. Un
              nome sbagliato non dà nessun errore — dà una casella storta. */}
          <label className="campo orizzontale">
            <input
              type="checkbox"
              checked={includiStornati}
              onChange={(e) => setIncludiStornati(e.target.checked)}
            />
            {/* ⭐ Dice SU COSA agisce: è la stessa cura della riga sotto il
                riepilogo, dall'altro capo. */}
            <span>anche gli storni nell’elenco</span>
          </label>
        </div>

        {/*
          🗣️🚨⭐⭐ **L'EXPORT DICE CHE PERIODO SI PORTA VIA, e fino al 18/09/2026
          non lo diceva.** Sua richiesta di quel giorno: *«mi serve la
          possibilità di fare un export personalizzato… devo potergli dare un
          periodo da me stabilito»*.
          📏 Misurato prima di toccare niente: **il periodo c'era già** — si
            sceglie la giornata e si riempie «fino a», e il file segue (provato:
            `cassa-2026-09-16_2026-09-18.csv`, sei righe su tre giornate).
          ⚖️ ⇒ Non mancava la funzione: mancava che si vedesse. Due bottoni muti
            in un angolo, e il periodo deciso da due comandi che stanno
            dall'altra parte della barra — chi non l'ha scritto non ha nessun
            motivo di credere che siano collegati.
          📌 *Una funzione che nessuno trova non è una funzione a metà: per chi
             la cerca è una funzione che non c'è.*
          ⚠️ E dice anche l'altra cosa che il file fa di testa sua: **gli storni
            ci sono sempre**, spunta o no. Era vero anche prima, e prima non lo
            diceva nessuno.
        */}
        <div className="azioni">
          <span className="tenue piccolo periodo-export">
            {filtro.from === filtro.to
              ? `il file copre ${giornoBreve(filtro.from)}`
              : `il file copre dal ${giornoBreve(filtro.from)} al ${giornoBreve(filtro.to)}`}
            {' · gli storni ci sono sempre'}
          </span>
          <button
            type="button"
            className="piatto"
            title={periodoPerEsteso(filtro.from, filtro.to)}
            onClick={() => void esportaCassa('csv', filtro)}
          >
            Esporta CSV
          </button>
          <button
            type="button"
            className="piatto"
            title={periodoPerEsteso(filtro.from, filtro.to)}
            onClick={() => void esportaCassa('xlsx', filtro)}
          >
            Esporta XLSX
          </button>
        </div>
      </div>

      {/* Più giornate insieme sono un caso raro: si dice a parole, e solo quando
          capita — nella testata ci sta il giorno da cui si parte. */}
      {filtro.from !== filtro.to && (
        <p className="tenue piccolo intervallo-cassa">
          Si stanno guardando più giornate insieme: da {giornoPerEsteso(filtro.from)} a{' '}
          {giornoPerEsteso(filtro.to)}.{' '}
          <button type="button" className="piatto" onClick={() => setFino(null)}>
            Torna a una sola
          </button>
        </p>
      )}

      <Guasto errore={riepilogo.error ?? righe.error} />

      {riepilogo.isPending ? (
        <Attesa cosa="il riepilogo" />
      ) : riepilogo.data ? (
        <>
          <Riepilogo r={riepilogo.data} />
          {/*
            🚨⭐⭐ **IL RIEPILOGO NON SEGUE I FILTRI, ED È GIUSTO COSÌ — ma
            finora non lo diceva nessuno, e sembrava rotto.**
            🗣️ 18/09/2026: *«mi sa il flag sugli storni non funziona»*. 📏 Misurato:
              togliendo la spunta l'elenco passava da 5 righe a 3, e **riepilogo,
              tabella per mezzo e file esportato restavano a 5**.
            ⚖️ Due di quei tre erano difetti e sono stati curati (il file adesso
              è la copia dell'elenco). **Il riepilogo no, e non va curato**:
              «nel cassetto, in contanti» deve **sempre** togliere gli storni,
              o conterebbe come presente denaro che è stato restituito. Un
              riepilogo che obbedisse alla spunta direbbe che nel cassetto c'è
              più di quanto ci sia — cioè l'unico errore che una cassa non può
              permettersi.
            ⇒ Resta intero, e **lo dichiara**. La differenza fra «non funziona»
              e «funziona così» sta tutta in una riga scritta.
            📌 *Un numero che non obbedisce a un comando vicino deve dire perché,
               o chi lo guarda conclude che il comando è rotto.*
          */}
          <p className="tenue piccolo nota-riepilogo">
            Questi numeri contano <strong>tutto il periodo</strong>, storni compresi: è quello che deve
            esserci nel cassetto. I filtri qui sopra cambiano l’elenco e il file, non il riepilogo.
          </p>
        </>
      ) : null}

      <h3>
        Le righe{' '}
        <span className="tenue piccolo">
          {righe.data?.total === null ? '' : `${righe.data?.total ?? 0} in tutto`}
        </span>
      </h3>
      {righe.isPending ? (
        <Attesa cosa="gli incassi" />
      ) : (righe.data?.items.length ?? 0) === 0 ? (
        <Vuoto
          titolo="Nessun incasso in questa giornata"
          perché="Non è un guasto: in questa giornata di cassa non è ancora stato registrato niente."
        />
      ) : (
        <div className={`tabella-scorre ${righe.isFetching ? 'in-aggiornamento' : ''}`}>
          <table className="tabella elenco-incassi">
            <thead>
              <tr>
                <th>ora</th>
                <th>chi</th>
                <th>partita</th>
                <th>mezzo</th>
                <th className="a-destra">importo</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {righe.data?.items.map((p) => (
                <RigaIncasso key={p.id} p={p} puòStornare={puòStornare} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * ⭐⭐ I TRE NUMERI, E PERCHÉ NON SONO UNO SOLO.
 *
 * Contanti e carta sono denaro che si è mosso **oggi**; il borsellino è denaro
 * incassato in un altro giorno, quando quel borsellino è stato ricaricato.
 * Sommarli darebbe una cifra che nel cassetto non c'è mai stata — e chi conta i
 * contanti a fine serata troverebbe una differenza che non sa spiegare.
 */
function Riepilogo({ r }: { r: ReturnType<typeof usaRiepilogo>['data'] & object }): React.JSX.Element {
  return (
    /*
     * 📐 DUE COLONNE, e non una fila lunga con mezza pagina vuota accanto.
     * 📏 Misurato: il riquadro è largo quanto la pagina (76rem) e la tabella per
     * mezzo ne occupava 28 — il resto era bianco, e i quattro numeri stavano
     * tutti a sinistra schiacciati uno sull'altro. I numeri e il dettaglio sono
     * due cose diverse: il primo si guarda a fine serata, il secondo quando il
     * primo non torna. ⇒ Stanno affiancati, e sotto i 60rem uno sotto l'altro.
     */
    <section className="riquadro riepilogo-cassa">
      <div className="totali">
        <div className="totale grosso">
          <span className="tenue piccolo">nel cassetto, in contanti</span>
          <strong className={r.contanteCents < 0 ? 'saldo-rosso' : ''}>{formatCents(r.contanteCents)}</strong>
        </div>
        <div className="totale grosso">
          <span className="tenue piccolo">con la carta</span>
          <strong>{formatCents(r.elettronicoCents)}</strong>
        </div>
        <div className="totale">
          <span className="tenue piccolo">pagato col borsellino</span>
          <strong>{formatCents(r.borsellinoSpesoCents)}</strong>
          <span className="tenue piccolo">incassato in un altro giorno</span>
        </div>
        {/* 🎁 Strada 3A (voce 33): lo sconto coi crediti premio, fuori dai totali. */}
        {r.premioUsatoCents !== 0 && (
          <div className="totale">
            <span className="tenue piccolo">🎁 Crediti premio usati</span>
            <strong className="crediti-piu">{formatCents(r.premioUsatoCents)}</strong>
            <span className="tenue piccolo">non sono un incasso</span>
          </div>
        )}
        {/* 🎾 16/10/2026: le quote che i promoter non pagano, fuori dai totali. */}
        {r.omaggioPromoterCents !== 0 && (
          <div className="totale">
            <span className="tenue piccolo">🎾 Omaggio promoter</span>
            <strong>{formatCents(r.omaggioPromoterCents)}</strong>
            <span className="tenue piccolo">
              {r.omaggioPromoterQuote === 1 ? '1 quota' : `${r.omaggioPromoterQuote} quote`} · non è un
              incasso
            </span>
          </div>
        )}
        <div className="totale">
          <span className="tenue piccolo">storni</span>
          <strong className={r.storni > 0 ? 'saldo-rosso' : 'tenue'}>
            {r.storni === 0 ? '—' : `${r.storni} · ${formatCents(r.stornatoCents)}`}
          </strong>
        </div>
      </div>

      <div className="a-destra-del-riepilogo">
        <table className="tabella per-metodo">
          <caption>per mezzo</caption>
          <tbody>
            {r.perMetodo.length === 0 ? (
              <tr>
                <td className="tenue">Nessuna riga in questa giornata.</td>
              </tr>
            ) : (
              r.perMetodo.map((m) => (
                <tr key={m.method}>
                  <td>{etichettaMetodo(m.method)}</td>
                  <td className="tenue piccolo">{m.righe} righe</td>
                  <td className="a-destra">{formatCents(m.totalCents)}</td>
                </tr>
              ))
            )}
            <tr className="somma">
              <td>
                <strong>tutto insieme</strong>
              </td>
              <td className="tenue piccolo">{r.righe} righe</td>
              <td className="a-destra">
                <strong>{formatCents(r.totalCents)}</strong>
              </td>
            </tr>
          </tbody>
        </table>
        {r.righe > 0 && <DaDoveArriva r={r} />}
      </div>
    </section>
  );
}

const PROVENIENZA: Record<Provenienza, { nome: string; cosa?: string }> = {
  // ⚠️ Qui dentro ci sono anche i tornei: si giocano e si pagano come una partita.
  partite: { nome: 'Partite' },
  maestri: { nome: 'Dai maestri', cosa: 'lezioni, allenamenti, corsi' },
  senza_prenotazione: { nome: 'Senza prenotazione', cosa: 'ricariche e restituzioni del borsellino' },
};

/**
 * 🧭 **Lo stesso «tutto insieme», diviso per provenienza** (voce 23).
 * 🗣️ *«troveremo sia l'incasso delle partite che l'incasso dei maestri»*.
 * ⚖️ Non è un secondo totale: le tre righe sommate fanno quello per mezzo, e
 *    la riga in fondo lo ripete apposta — se un giorno non tornasse, lo si
 *    vedrebbe qui, uno sotto l'altro.
 */
function DaDoveArriva({ r }: { r: ReturnType<typeof usaRiepilogo>['data'] & object }): React.JSX.Element {
  return (
    <table className="tabella per-provenienza">
      <caption>da dove arriva</caption>
      <tbody>
        {r.perProvenienza.map((x) => (
          <tr key={x.provenienza}>
            <td>
              {PROVENIENZA[x.provenienza].nome}
              {PROVENIENZA[x.provenienza].cosa && (
                <span className="cosa">{PROVENIENZA[x.provenienza].cosa}</span>
              )}
            </td>
            <td className="tenue piccolo">{x.righe} righe</td>
            <td className={`a-destra${x.totalCents < 0 ? ' saldo-rosso' : ''}`}>
              {formatCents(x.totalCents)}
            </td>
          </tr>
        ))}
        <tr className="somma">
          <td>
            <strong>tutto insieme</strong>
          </td>
          <td className="tenue piccolo">{r.righe} righe</td>
          <td className="a-destra">
            <strong>{formatCents(r.totalCents)}</strong>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function RigaIncasso({ p, puòStornare }: { p: Incasso; puòStornare: boolean }): React.JSX.Element {
  const storna = usaStornoIncasso(p.bookingId);
  const [conferma, setConferma] = useState(false);
  const [motivo, setMotivo] = useState('');

  const èUnoStorno = p.voidsPaymentId !== null;

  return (
    <tr className={èUnoStorno ? 'storno' : p.status === 'voided' ? 'stornato' : ''}>
      <td className="tenue piccolo">{new Date(p.recordedAt).toLocaleTimeString('it-IT').slice(0, 5)}</td>
      {/*
        🗣️ *«rendimi i nomi che sono presenti nella cassa cliccabili: se clicco
        sul nome apro la partita relativa a quel pagamento»* (17/09/2026).
        ⚠️ Solo quando una partita C'È: una ricarica del borsellino non nasce da
        nessuna prenotazione, e un nome che sembra cliccabile e non porta da
        nessuna parte è peggio di un nome che non lo sembra.
        📅 Il giorno lo porta la RIGA (`bookingDate`), e non è quello di cassa:
        un incasso di lunedì può pagare una partita di sabato — col giorno di
        cassa si aprirebbe la scheda giusta sopra la giornata sbagliata.
      */}
      <td>
        {p.bookingId !== null && p.bookingDate !== null && p.playerName !== null ? (
          <Link
            className="nome-in-cassa"
            to={`/calendario?data=${p.bookingDate}&scheda=${p.bookingId}`}
            title="Apri la partita di questo pagamento"
          >
            {p.playerName}
          </Link>
        ) : (
          (p.playerName ?? '—')
        )}
        {p.reason && <div className="tenue piccolo">{p.reason}</div>}
      </td>
      <td className="tenue piccolo">{p.bookingLabel ?? '—'}</td>
      <td>{etichettaMetodo(p.method)}</td>
      <td className={`a-destra ${p.amountCents < 0 ? 'saldo-rosso' : ''}`}>{formatCents(p.amountCents)}</td>
      <td>
        {/*
          ⛔ Una riga di denaro non si cancella: si storna. E lo storno mostra
          COSA sta stornando (§ 03.5) — la cifra e chi — prima di chiedere
          conferma: «sei sicuro?» da solo non dà a nessuno modo di accorgersi di
          aver puntato alla riga sbagliata.
        */}
        {puòStornare && !èUnoStorno && p.status === 'paid' && !conferma && (
          <button type="button" className="piatto pericolo" onClick={() => setConferma(true)}>
            Storna
          </button>
        )}
        {p.status === 'voided' && <span className="tenue piccolo">stornato</span>}
        {èUnoStorno && <span className="tenue piccolo">storno</span>}
        {conferma && (
          <div className="conferma">
            <p>
              Stornare <strong>{formatCents(p.amountCents)}</strong> di {p.playerName ?? 'questa riga'} (
              {etichettaMetodo(p.method)})? Resteranno due righe: questa e lo storno.
            </p>
            <input
              value={motivo}
              placeholder="perché (facoltativo)"
              onChange={(e) => setMotivo(e.target.value)}
            />
            <Guasto errore={storna.error} />
            <div className="azioni">
              <button
                type="button"
                className="pericolo"
                disabled={storna.isPending}
                onClick={() =>
                  storna.mutate(
                    { id: p.id, reason: motivo.trim() === '' ? null : motivo.trim() },
                    { onSuccess: () => setConferma(false) },
                  )
                }
              >
                {storna.isPending ? 'Un attimo…' : 'Sì, storna'}
              </button>
              <button type="button" className="piatto" onClick={() => setConferma(false)}>
                No
              </button>
            </div>
          </div>
        )}
      </td>
    </tr>
  );
}
